import React from 'react'
import { Box, Flex, Heading } from 'pcln-design-system'
import { Carousel } from 'pcln-carousel'
import styled from 'styled-components'
import MediaCard from '../MediaCard/MediaCard'

const MediaRowWrapper = styled(Flex)`
  column-gap: 18px;
`

const MediaCardWrapper = styled(Box)`
  flex: 0 0 auto;
`

/**
 * MediaCardCollection - A component that renders a horizontal scrollable collection of media cards
 *
 * @component
 * @param {Object} props
 * @param {Object} collection - The collection data object
 * @param {string} collection.headline - Optional headline text for the collection
 * @param {boolean} props.isCarousel - Optional boolean to render the collection as a carousel
 * @param {Array}  collection.items - Array of media content items to display
 * @param {React.ComponentType} [props.cardNode] - Optional custom card component to override default MediaCard
 *                                                If provided, it will receive both spread mediaContent props
 *                                                and a content prop with the full mediaContent object
 *
 * @example
 *
 * return (
 *   <MediaCardCollection
 *     collection={collection}
 *     cardNode={CustomCard}
 *   />
 * )
 *
 * @returns {JSX.Element | null} Returns null if collection or items array is empty
 */
export default function MediaCardCollection({ collection, cardNode, isCarousel }) {
  const CardComponent = cardNode
  return collection && collection?.items?.length ? (
    <Box my={[3, 0]}>
      {collection?.headline ? (
        <Heading color='text.heading' m={0} mb={[0, 2]} textStyle='heading2'>
          {collection.headline}
        </Heading>
      ) : null}
      {isCarousel ? (
        <Box style={{overflow: 'hidden'}}>
        <Carousel
          arrowsPosition='side'
          onSlideChange={function () { }}
          currentSlide={0}
          displayArrowsMobile
          slideSpacing={2}
          overflowAllowancePxX={16}
          visibleSlides={[1.2, 2.5, 3.1, 3.5, 3.5]}
          mobileVisibleSlides={[1.2, 2.1]}
        >
          {collection.items.map((mediaContent) => (
            <MediaCardWrapper key={`${mediaContent?.internalName}`}>
              {CardComponent ? (
                <CardComponent content={mediaContent} {...mediaContent} />
              ) : (
                <MediaCard {...mediaContent} />
              )}
            </MediaCardWrapper>
          ))}
        </Carousel>
        </Box>
      ) : (
        <MediaRowWrapper py={2} my={3} overflow={['scroll', 'visible']} px={3} mx={-3}>
          {collection.items.map((mediaContent, i) => (
            <MediaCardWrapper
              key={`${i}-${mediaContent?.internalName}`}
              width={['75vw', '100%']}
              display={['flex', 'contents']}
            >
              {CardComponent ? (
                <CardComponent content={mediaContent} {...mediaContent} />
              ) : (
                <MediaCard {...mediaContent} />
              )}
            </MediaCardWrapper>
          ))}
        </MediaRowWrapper>
      )}
    </Box>
  ) : null
}